<template>
  <div>
    <div v-if="form.TBM정보.postId" class="w-full grid xl:flex xl:justify-end items-center mb-6 gap-2">
      <span class="w-full xl:w-auto flex flex-wrap">
        <span class="font-bold">연결된 일 작업허가서:</span>
        <span class="pl-2">{{ 연결된일작업허가서 }}</span>
      </span>
      <div class="grid xl:flex items-center gap-2">
        <vs-button v-if="!readonly" class="w-full xl:w-auto p-2" color="#EDEDED" text-color="black" type="filled" @click="onClickCancel">취소</vs-button>
      </div>
    </div>

    <!--기본정보-->
    <field-select-module v-model="form.stdInfo" :board="board" :is-print="isPrint" :readonly="readonly" :field-readonly="readonly" />

    <!-- TBM 정보 -->
    <div class="mt-6 mb-3">
      <h5><i class="dot"></i>TBM 정보</h5>
    </div>
    <div class="writeGridContainer">
      <div>
        <div>TBM 실시일</div>
        <div>
          <span v-if="isPrint" class="print-input"> {{ form.TBM정보.TBM실시일 }} </span>
          <date-selector v-else class="w-full" v-model="form.TBM정보.TBM실시일" :readonly="readonly" />
        </div>
      </div>
      <div>
        <div>TBM 실시시간</div>
        <div>
          <div v-if="isPrint" class="flex gap-2">
            <span class="print-input">{{ form.TBM정보.TBM실시시간.sTime }}</span>
            <span style="align-self: center">~</span>
            <span class="print-input">{{ form.TBM정보.TBM실시시간.eTime }}</span>
          </div>
          <time-selector-rang v-else class="w-full" v-model="form.TBM정보.TBM실시시간" :readonly="readonly" />
        </div>
      </div>
      <div>
        <div>작업날짜와 동일함</div>
        <div>
          <radio-group class="w-full" flex :items="['예', '아니오']" v-model="form.TBM정보.작업날짜와동일함" :readonly="readonly" />
        </div>
      </div>
      <div>
        <div>작업명</div>
        <div>
          <span v-if="isPrint" class="print-input">{{ form.TBM정보.작업명 }}</span>
          <ml-input v-else class="w-full" v-model="form.TBM정보.작업명" :readonly="readonly" />
        </div>
      </div>
      <div>
        <div>폭염 작업 여부</div>
        <div>
          <radio-group class="w-full" flex :items="['Y', 'N']" v-model="form.TBM정보.폭염작업여부" :readonly="readonly" />
        </div>
      </div>
      <div>
        <div>밀폐공간 작업 여부</div>
        <div>
          <radio-group class="w-full" flex :items="['Y', 'N']" v-model="form.TBM정보.밀폐공간작업여부" :readonly="readonly" />
        </div>
      </div>
    </div>

    <!-- 폭염작업여부 y 시-->
    <div v-if="form.TBM정보.폭염작업여부 === 'Y'" class="mt-6 mb-3">
      <table v-if="isPrint">
        <thead>
          <tr>
            <th>측정시간</th>
            <th>측정자</th>
            <th>온도(℃)</th>
            <th>습도(%)</th>
            <th>체감온도(℃)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(item, index) in form.TBM정보.폭염작업" :key="`heat_work_${index}`">
            <td>{{ item.작업시간 }}</td>
            <td>{{ item.측정자 }}</td>
            <td>{{ item.온도 }}</td>
            <td>{{ item.습도 }}</td>
            <td style="font-weight: 600">{{ item.체감온도 }}</td>
          </tr>
        </tbody>
      </table>
      <table-custom
        v-else
        :board="board"
        :is-print="isPrint"
        :tableData="[
          { id: '작업시간', title: '측정시간', type: 'time' },
          { id: '측정자', title: '측정자', type: 'text' },
          {
            id: '온도',
            title: '온도(℃)',
            type: 'number',
            decimalPlaces: 1,
            readonlyIndexList: heatWorkReadonlyRows,
            max: 50,
            min: -20,
            inputFunction: calculateHeatWorkFeelTemperature,
          },
          {
            id: '습도',
            title: '습도(%)',
            type: 'number',
            decimalPlaces: 1,
            readonlyIndexList: heatWorkReadonlyRows,
            max: 100,
            min: 0,
            inputFunction: calculateHeatWorkFeelTemperature,
          },
          {
            id: '체감온도',
            title: '체감온도(℃)',
            type: 'number',
            decimalPlaces: 1,
            contentStyle: { 'font-weight': 600 },
            max: 100,
            min: -50,
            inputFunction: (data, tr, cellData, indextr) => {
              updateHeatWorkReadonly(indextr, data);
            },
          },
        ]"
        :mobile="['측정자', '체감온도']"
        v-model="form.TBM정보.폭염작업"
      >
        <template #customTitle>
          <p class="font-medium">폭염작업</p>
        </template>
      </table-custom>
    </div>

    <!-- 밀폐공간 작업 여부 y 시 -->
    <div v-if="form.TBM정보.밀폐공간작업여부 === 'Y'" class="mt-6 mb-3">
      <TableInspectMerge
        :is-print="isPrint"
        :is-readonly="board.readonly"
        tableHeaderTitle="교육항목"
        :tableHeaderCheck="[{ text: '숙지' }, { text: '미숙지' }]"
        :tableHeaderNote="[{ text: '비고' }]"
        v-model="밀폐공간교육항목"
        @input="confinedSpaceTrainingItems"
        class="mt-28px"
      >
        <template #title>
          <p class="font-medium">밀폐공간 교육항목 숙지여부</p>
        </template>
      </TableInspectMerge>
    </div>

    <div class="writeGridContainer">
      <div class="writeGridLineItem">
        <div>작업내용</div>
        <div>
          <span v-if="isPrint" class="print-textarea">{{ form.TBM정보.작업내용 }}</span>
          <ml-textarea v-else class="w-full" v-model="form.TBM정보.작업내용" :readonly="readonly" />
        </div>
      </div>
      <div>
        <div>TBM 장소</div>
        <div>
          <span v-if="isPrint" class="print-input">{{ form.TBM정보.TBM장소 }}</span>
          <ml-input v-else class="w-full" v-model="form.TBM정보.TBM장소" :readonly="readonly" />
        </div>
      </div>
      <div>
        <div class="whitespace-no-wrap">위험성평가 실시여부</div>
        <div>
          <radio-group class="w-full" flex :items="['예', '아니오']" v-model="form.TBM정보.위험성평가실시여부" :readonly="readonly" />
        </div>
      </div>
    </div>

    <!-- 잠재위험요인 -->
    <div class="mt-28px">
      <div v-if="isPrint">
        <div class="mb-3">
          <h5><i class="dot"></i>잠재위험요인</h5>
        </div>
        <table>
          <colgroup>
            <col width="81" />
            <col />
            <col />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>연번</th>
              <th>잠재위험요인</th>
              <th>현재관리상태</th>
              <th>감소대책(중요위험)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in form.잠재위험요인" :key="`item_${index}`">
              <td>{{ index + 1 }}</td>
              <td>{{ item.잠재위험요인 }}</td>
              <td>{{ item.현재관리상태 }}</td>
              <td>{{ item.대책 }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <table-custom
        v-else
        :board="board"
        :is-print="isPrint"
        title="잠재위험요인"
        :tableData="[
          { id: 'no', title: '연번', type: 'no' },
          { id: '잠재위험요인', title: '잠재위험요인', type: 'text', max: 10000, invisible: true },
          { id: '현재관리상태', title: '현재관리상태', type: 'text', max: 10000, invisible: true },
          { id: '대책', title: '감소대책(중요위험)', type: 'text', max: 10000, invisible: true },
          { id: '위험성평가', title: '위험성평가', type: 'customType', width: '80' },
        ]"
        :mobile="['잠재위험요인']"
        v-model="form.잠재위험요인"
      >
        <template #colgroup="{ isMobile }">
          <col width="40px" />
          <col v-if="!isMobile" width="81px" />
          <col width="" />
          <col v-if="!isMobile" />
          <col v-if="!isMobile" />
          <col :width="isMobile ? '40px' : '80px'" />
        </template>
        <template #buttons="{ handleAddButton, handleRemoveButton, checkData }">
          <div class="w-full flex flex-wrap justify-end gap-x-4 gap-y-2">
            <select-risk-assessment-db-button class="w-full sm:w-auto" @select="onSelectProcess" />
            <vs-button class="flex-1" color="secondary" @click="handleAddButton"> 항목추가 </vs-button>
            <vs-button class="flex-1" color="secondary" type="border" :disabled="form.잠재위험요인.length < 1 || checkData.length === 0" @click="handleRemoveButton"> 선택삭제 </vs-button>
          </div>
        </template>
        <template #customType="{ value, isMobile }">
          <template v-if="value">
            <vs-button v-if="isMobile" class="w-full" color="secondary" type="border" icon-pack="feather" icon="icon-link" @click="onMoveWrite(value.projectId, value.boardId, value.postId)"> 위험성평가 바로가기 </vs-button>
            <div v-else class="pt-2 cursor-pointer" @click="onMoveWrite(value.projectId, value.boardId, value.postId)">
              <feather-icon icon="LinkIcon" style="width: 18px" />
            </div>
          </template>
          <p v-else-if="isMobile" class="py-2 text-sm text-gray-500">연결된 위험성평가 없음</p>
        </template>
      </table-custom>
    </div>

    <!-- 중점위험요인 -->
    <div class="mt-6 mb-3">
      <h5><i class="dot"></i>중점위험요인</h5>
    </div>
    <div class="writeGridContainer">
      <div class="writeGridLineItem">
        <div>선정</div>
        <div>
          <span v-if="isPrint" class="print-input">{{ form.중점위험요인.선정 }}</span>
          <ml-input v-else class="w-full" v-model="form.중점위험요인.선정" :readonly="readonly" />
        </div>
      </div>
      <div class="writeGridLineItem">
        <div>대책</div>
        <div>
          <span v-if="isPrint" class="print-textarea">{{ form.중점위험요인.대책 }}</span>
          <ml-textarea v-else class="w-full" v-model="form.중점위험요인.대책" :readonly="readonly" />
        </div>
      </div>
    </div>

    <!-- TBM 리더 확인 -->
    <div class="mt-6 mb-3">
      <h5><i class="dot"></i>TBM 리더 확인</h5>
    </div>
    <div class="writeGridContainer">
      <div>
        <div>소속명</div>
        <div>
          <span v-if="isPrint" class="print-input">{{ form.TBM리더확인.소속명 }}</span>
          <ml-input v-else class="w-full" v-model="form.TBM리더확인.소속명" :readonly="readonly" />
        </div>
      </div>
      <div>
        <div>직책</div>
        <div>
          <span v-if="isPrint" class="print-input">{{ form.TBM리더확인.직책 }}</span>
          <ml-input v-else class="w-full" v-model="form.TBM리더확인.직책" :readonly="readonly" />
        </div>
      </div>
      <div>
        <div>성명</div>
        <div>
          <span v-if="isPrint" class="print-input">{{ form.TBM리더확인.성명 }}</span>
          <ml-input v-else class="w-full" v-model="form.TBM리더확인.성명" :readonly="readonly" />
        </div>
      </div>
      <div>
        <div>서명</div>
        <div>
          <sign-field flex-start v-model="form.TBM리더확인.서명" :readonly="readonly" />
        </div>
      </div>
    </div>

    <!-- 작업 전 안전조치 확인 -->
    <div class="mt-28px">
      <div v-if="isPrint">
        <div class="flex items-center">
          <h5 class="mb-4"><i class="dot"></i>작업 전 안전조치 확인</h5>
          <span class="ml-2 mb-4">※위 잠재위험요인(중점위험 포함) 안전조치 여부 재확인</span>
        </div>
        <table>
          <colgroup>
            <col />
            <col width="200" />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>잠재위험요소</th>
              <th>조치여부</th>
              <th>‘아니오’인 경우 조치 내용</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in form.작업전안전조치확인" :key="`item2_${index}`">
              <td>{{ item.잠재위험요소 }}</td>
              <td>
                <radio-group class="w-full justify-center" flex :items="['예', '아니오']" v-model="item.조치여부" />
              </td>
              <td>{{ item.조치여부 === "아니오" ? item.조치내용 : "" }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <table-custom
        v-else
        :board="board"
        :is-print="isPrint"
        :tableData="[
          { id: '잠재위험요소', title: '잠재위험요소(중점위험 포함)', type: 'text', max: 10000, invisible: true },
          { id: '조치여부', title: '조치여부', type: 'radio', radioItems: [{ value: '예' }, { value: '아니오' }], defaultValue: '예' },
          { id: '조치내용', title: '‘아니오’인 경우 조치 내용', type: 'customType' },
        ]"
        :mobile="['잠재위험요소']"
        :minSize="0"
        :defaultSize="0"
        v-model="form.작업전안전조치확인"
        class="mt-28px"
      >
        <template #colgroup="{ isMobile }">
          <col width="40px" />
          <col width="" />
          <col v-if="!isMobile" width="200px" />
          <col v-if="!isMobile" width="" />
          <col v-if="isMobile" width="40px" />
        </template>
        <template #customTitle>
          <h5 class="flex items-center mb-4"><i class="dot"></i>작업 전 안전조치 확인</h5>
          <span class="ml-2 mb-4">※위 잠재위험요인(중점위험 포함) 안전조치 여부 재확인</span>
        </template>
        <template #customType="{ index, trData, id, input }">
          <ml-input v-if="form.작업전안전조치확인[index].조치여부 === '아니오'" class="w-full" v-model="form.작업전안전조치확인[index].조치내용" :max="10000" />
        </template>
      </table-custom>
    </div>

    <!-- 작업 전 일일 안전점검 시행 결과 -->
    <div class="mt-28px">
      <div v-if="isPrint">
        <div class="mb-3">
          <h5><i class="dot"></i>작업 전 일일 안전점검 시행 결과</h5>
        </div>
        <table>
          <colgroup>
            <col />
            <col width="200" />
            <col />
          </colgroup>
          <thead>
            <tr>
              <th>점검항목</th>
              <th>숙지여부</th>
              <th>비고</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in form.작업전일일안전점검시행결과_점검항목" :key="`item3_${index}`">
              <td>{{ item.점검항목 }}</td>
              <td>{{ item.숙지여부 }}</td>
              <td>{{ item.비고 }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <table-custom
        v-else
        :board="board"
        :is-print="isPrint"
        :tableData="[
          { id: '점검항목', title: '점검항목', type: 'label' },
          { id: '숙지여부', title: '숙지여부', type: 'radio', radioItems: [{ value: '예' }, { value: '아니오' }], width: '200px' },
          { id: '비고', title: '비고', type: 'text', max: 10000, invisible: true },
        ]"
        v-model="form.작업전일일안전점검시행결과_점검항목"
        :controls="false"
        :mobile="['점검항목', '숙지여부']"
        title="작업 전 일일 안전점검 시행 결과"
        class="mt-28px"
      />
    </div>

    <!-- 참석자 명단 -->
    <div class="mt-28px">
      <div v-if="isPrint">
        <div class="flex items-center">
          <h5 class="mb-4"><i class="dot"></i>참석자 명단</h5>
          <span class="ml-2 mb-4">※TBM에 참여하지 않은 작업자를 확인하여 미팅 참석 유도</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>No.</th>
              <th>부서(업체)명</th>
              <th>소속</th>
              <th>직종</th>
              <th>이름</th>
              <th>휴대폰번호</th>
              <th>서명</th>
              <th>비고</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in form.참석자명단" :key="`item4_${index}`">
              <td>{{ index + 1 }}</td>
              <td>{{ item.업체명 }}</td>
              <td>{{ item.소속 }}</td>
              <td>{{ item.직종 }}</td>
              <td>{{ item.이름 }}</td>
              <td>{{ item.휴대폰번호 }}</td>
              <td>
                <img v-if="!!`${item.서명}`.trim()" :src="item.서명" alt="서명" style="width: 100px; height: 50px" />
              </td>
              <td>{{ item.비고 }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <table-custom
        v-else
        class="mt-6 mb-5"
        :board="board"
        :is-print="isPrint"
        :default-size="0"
        :min-size="0"
        :tableData="[
          { id: 'no', title: 'No.', type: 'no' },
          { id: '업체명', title: '부서(업체)명', type: 'fixed-text' },
          { id: '소속', title: '소속', type: 'fixed-text' },
          { id: '직종', title: '직종', type: 'fixed-text' },
          { id: '이름', title: '이름', type: 'fixed-text' },
          { id: '휴대폰번호', title: '휴대폰번호', type: 'fixed-text' },
          { id: '서명', title: '서명', type: 'sign' },
          { id: '비고', title: '비고', type: 'text' },
        ]"
        v-model="form.참석자명단"
        :mobile="['업체명', '소속', '이름']"
      >
        <template #customTitle>
          <h5 class="flex items-center mb-4"><i class="dot"></i>참석자 명단</h5>
          <span class="ml-2 mb-4">※TBM에 참여하지 않은 작업자를 확인하여 미팅 참석 유도</span>
        </template>
        <template #colgroup="{ isMobile }">
          <col v-if="isMobile" width="40px" />
          <col v-if="!isMobile" width="100px" />
          <col v-if="!isMobile" width="" />
          <col v-if="!isMobile" width="" />
          <col width="" />
          <col width="" />
          <col width="" />
          <col width="" />
          <col v-if="!isMobile" width="" />
          <col v-if="isMobile" width="40px" />
        </template>
        <template #buttons="{ handleAddButton, handleRemoveButton, checkData }">
          <vs-button color="secondary" class="ml-4" @click="onClickLoadQR"> QR 참석자 명단 가져오기 </vs-button>
          <vs-button color="secondary" type="border" :disabled="form.참석자명단.length < 1 || checkData.length === 0" class="ml-4" @click="handleRemoveButton"> 선택삭제 </vs-button>
        </template>
      </table-custom>
    </div>

    <!--    사진대지-->
    <PhotoUploadGridModule :row-count="2" :grid-count="2" :is-print="isPrint" :readonly="board.readonly" section-title="사진대지" :showFooterInput="false" v-model="form.photoList" />

    <!--    휴게사진-->
    <PhotoUploadGridModule :row-count="2" :grid-count="2" :is-print="isPrint" :readonly="board.readonly" section-title="휴게사진" placeholder="(예시 휴게시간 > 00:00 ~ 00:20)" v-model="form.photoList2" />

    <!-- 파일첨부 -->
    <file-upload-module :readonly="board.readonly" v-model="form.fileList" :is-print="isPrint" class="mt-8" />

    <popup-q-r-history v-model="showQRHistory" :search-date="form.TBM정보.TBM실시일" :selected-user-ids="selectedUserIds" @select="onSelectHistory" />
    <popup-daily-safe-work-permit ref="popupDailySafeWorkPermit" :search-date="form.TBM정보.TBM실시일" v-model="showDailySafeWorkPermit" @select="onSelectDailySafeWorkPermit" />
  </div>
</template>

<script>
import FieldSelectModule from "@/views/modules/FieldSelectModule";
import FileUploadModule from "@/views/modules/FileUploadModule.vue";
import WriteGridContainer from "@/components/WriteGridContainer.vue";
import DateSelector from "@/components/selector/DateSelector.vue";
import RadioGroup from "@/components/radio/RadioGroup.vue";
import SignField from "@/components/SignField.vue";
import TableCustom from "@/components/TableCustom.vue";
import AddRowLayout from "@/components/layout/AddRowLayout.vue";
import SelectRiskAssessmentDbButton from "@/views/boards/single-page/830/SelectRiskAssessmentDbButton.vue";
import TimeSelectorRang from "@/components/selector/TimeSelectorRang.vue";
import PopupQRHistory from "@/popup/PopupQRHistory.vue";
import PopupDailySafeWorkPermit from "@/popup/PopupDailySafeWorkPermit.vue";
import NumberInput from "@/components/NumberInput.vue";
import { required } from "vuelidate/lib/validators";
import TemperatureFeelTablePopup from "@/views/boards/single-page/830/TemperatureFeelTablePopup.vue";
import TableInspectMerge from "@/components/TableInspectMerge.vue";

export default {
  components: {
    TableInspectMerge,
    TemperatureFeelTablePopup,
    NumberInput,
    PopupDailySafeWorkPermit,
    PopupQRHistory,
    TimeSelectorRang,
    SelectRiskAssessmentDbButton,
    AddRowLayout,
    TableCustom,
    SignField,
    RadioGroup,
    DateSelector,
    WriteGridContainer,
    FileUploadModule,
    FieldSelectModule,
  },
  props: {
    isPrint: Boolean,
    board: Object,
    value: Object,
    permission: Object,
  },
  validations: {
    form: {
      TBM정보: {
        폭염작업여부: { required },
        폭염작업: {
          required,
          heatWorkValidation(value) {
            // 폭염작업여부가 'Y'가 아니면 검증 통과
            if (this.form.TBM정보.폭염작업여부 !== "Y") {
              return true;
            }

            // 폭염작업 배열에서 체감온도 확인
            if (Array.isArray(this.form.TBM정보.폭염작업)) {
              for (const item of this.form.TBM정보.폭염작업) {
                if (!item.체감온도 || item.체감온도 <= 31) {
                  return true; // 31 이하면 검증 실패 -> 해당조건 취소
                }
              }
            }

            return true; // 모든 체감온도가 31 초과면 검증 통과
          },
        },
      },
    },
  },
  data() {
    return {
      showQRHistory: false,
      showDailySafeWorkPermit: false,
      form: {
        stdInfo: {},
        TBM정보: {
          postId: 0,
          boardId: 0,
          TBM실시일: "",
          TBM실시시간: { sTime: "", eTime: "" },
          작업날짜와동일함: "아니오",
          작업명: "",
          작업내용: "",
          TBM장소: "",
          위험성평가실시여부: "예",
          raw: {}, // 실제 사용하는 곳은 없으나, 추후에 일안전작업허가서와 연동할 것을 대비하여 추가
          폭염작업여부: "N",
          폭염작업: [],
          밀폐공간작업여부: "N",
          밀폐공간작업: {},
        },
        잠재위험요인: [],
        중점위험요인: {
          선정: "",
          대책: "",
        },
        TBM리더확인: {
          소속명: "",
          직책: "",
          성명: "",
          서명: "",
        },
        작업전안전조치확인: [],
        작업전일일안전점검시행결과_점검항목: [
          { id: 1, 점검항목: "작업자들의 중점위험요인 숙지여부", 숙지여부: "예", 비고: "" },
          { id: 2, 점검항목: "중점잠재위험요인 관련 대책 숙지여부", 숙지여부: "예", 비고: "" },
        ],
        참석자명단: [],
        photoList: [],
        photoList2: [], // 휴게사진
        fileList: [],

        연결된일작업허가서: null,
        TBM상태: "",
      },

      heatWorkReadonlyRows: [],
      밀폐공간교육항목: [
        {
          code: "밀폐공간_교육항목_001",
          content: "밀폐공간의 위험성",
          isChecked: false,
        },
        {
          code: "밀폐공간_교육항목_002",
          content: "산소 및 유해가스 농도 측정에 관한 사항",
          isChecked: false,
        },
        {
          code: "밀폐공간_교육항목_003",
          content: "환기설비의 가동 등 안전한 작업방법에 관한 사항",
          isChecked: false,
        },
        {
          code: "밀폐공간_교육항목_004",
          content: "보호구의 착용과 사용방법에 관한 사항",
          isChecked: false,
        },
        {
          code: "밀폐공간_교육항목_005",
          content: "사고 시 구조요청을 할 수 있는 관할 소방관서 등 비상연락처 및 응급조치에 관한 사항",
          isChecked: false,
        },
        {
          code: "밀폐공간_교육항목_006",
          content: "제625조에 따른 대피용 기구의 위치 및 사용방법",
          isChecked: false,
        },
      ],
    };
  },
  computed: {
    isMobile() {
      return this.$viewport.isMobile;
    },
    readonly() {
      return this.board.readonly || this.isPrint;
    },
    연결된일작업허가서() {
      let 연결된일작업허가서 = "";
      if (this.form.TBM정보.boardId === 976) {
        연결된일작업허가서 += "외부공사·작업";
      }
      if (this.form.TBM정보.boardId === 981) {
        연결된일작업허가서 += "내부작업(직영)";
      }
      if (this.form.TBM정보.raw.사업장명) {
        연결된일작업허가서 += `, ${this.form.TBM정보.raw.사업장명}`;
      }
      if (this.form.TBM정보.raw.작업내용) {
        연결된일작업허가서 += `, ${this.form.TBM정보.raw.작업내용}`;
      }

      this.form.연결된일작업허가서 = 연결된일작업허가서;
      return 연결된일작업허가서;
    },
    selectedUserIds() {
      return this.form.참석자명단.filter((item) => item.user_id).map((item) => item.user_id);
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(value) {
        const keys = Object.keys(this.form);
        for (const key of keys) {
          if (value[key]) {
            this.form[key] = value[key];
          }
        }

        // "현재관리상태" 컬럼이 없던 시절에 작성된 문서는 해당 값이 없으므로, 빈 값으로 채워
        // 입력 시 반응형으로 동작하도록 한다.
        for (const item of this.form.잠재위험요인) {
          if (item.현재관리상태 === undefined) {
            this.$set(item, "현재관리상태", "");
          }
        }

        this.loadConfinedSpaceTrainingFromDB();

        // 작성페이지의 상단 버튼들을 다시 그릴 수 있도록 이벤트를 발생시킨다.
        const event = new CustomEvent("refreshButtons");
        window.dispatchEvent(event);
      },
    },
    form: {
      immediate: true,
      deep: true,
      handler(value) {
        this.$emit("input", value);

        // 폭염작업여부가 설정되지 않은 경우 기본값을 "N"으로 설정
        if (!value.TBM정보.폭염작업여부) {
          this.form.TBM정보.폭염작업여부 = "N";
        }

        // 밀폐 공간작업여부가 설정되지 않은 경우 기본값을 "N"으로 설정
        if (!value.TBM정보.밀폐공간작업여부) {
          this.form.TBM정보.밀폐공간작업여부 = "N";
        }
      },
    },
    "form.잠재위험요인": {
      deep: true,
      handler() {
        const 작업전안전조치확인 = this.form.작업전안전조치확인.filter((item) => {
          return !item.잠재위험요인_id;
        });
        this.form.작업전안전조치확인 = [];
        for (let i = 0; i < this.form.잠재위험요인.length; i++) {
          this.form.작업전안전조치확인.push({
            잠재위험요인_id: this.form.잠재위험요인[i].id,
            잠재위험요소: this.form.잠재위험요인[i].잠재위험요인,
            조치여부: "예",
            조치내용: "",
          });
        }
        this.form.작업전안전조치확인.push(...작업전안전조치확인);
      },
    },

    // TBM 상태(완료/미진행)를 목록(List.js getCol)과 동일한 기준으로 계산하여 form 에 자동 반영한다.
    "form.참석자명단": {
      deep: true,
      immediate: true,
      handler() {
        this.updateTBM상태();
      },
    },
    "form.photoList": {
      deep: true,
      immediate: true,
      handler() {
        this.updateTBM상태();
      },
    },
    "form.TBM정보.TBM실시시간": {
      deep: true,
      immediate: true,
      handler() {
        this.updateTBM상태();
      },
    },

    // 온도나 습도가 변경될 때마다 체감온도를 실시간으로 계산
    // "form.TBM정보.폭염작업": {
    //   deep: true,
    //   handler(newVal) {
    //     if (newVal && newVal.length > 0) {
    //       newVal.forEach((item, index) => {
    //         if ((item.온도 !== undefined && item.온도 !== null) || (item.습도 !== undefined && item.습도 !== null)) {
    //           this.calculateTemperatureFeelForItem(item, index);
    //         }
    //       });
    //     }
    //   },
    // },
  },
  created() {
    const keys = Object.keys(this.form);
    for (const key of keys) {
      if (this.value[key]) {
        this.form[key] = this.value[key];
      }
    }
    this.loadConfinedSpaceTrainingFromDB();
  },
  methods: {
    // List.js getCol(index===7) 과 동일한 기준으로 TBM 상태를 계산하여 form.TBM상태 에 반영한다.
    updateTBM상태() {
      const 참석자명단 = this.form.참석자명단;
      const 사진대지 = this.form.photoList.filter((e) => e.id);
      const TBM실시시간 = this.form.TBM정보.TBM실시시간;

      this.form.TBM상태 = 참석자명단.length === 0 || 사진대지.length === 0 || !TBM실시시간.eTime ? "미진행" : "완료";
    },
    onSelectProcess(model) {
      // 값이 비어있는 row 삭제
      this.form.잠재위험요인 = this.form.잠재위험요인.filter((item) => item.잠재위험요인 !== "");

      if (model.length > 0) {
        for (const item of model) {
          this.addRowPotentialRiskFactors(item);
        }
      }
    },
    addRowPotentialRiskFactors(item) {
      this.form.잠재위험요인.push({
        id: this.form.잠재위험요인.length + 1,
        잠재위험요인: item ? item.위험요인 : "",
        현재관리상태: item ? item.현재관리상태 : "",
        대책: item ? item.감소대책 : "",
        위험성평가: item ? { projectId: item.projectId, boardId: item.boardId, postId: item.postId } : false,
      });
    },
    onMoveWrite(projectId, boardId, postId) {
      const width = window.screen.availWidth;
      const height = window.screen.availHeight;
      const left = 0;
      const top = 0;
      window.open(`/project/${projectId}/tab/${boardId}/write/${postId}`, "blank", `width=${width},height=${height},left=${left},top=${top}`);
    },
    onSelectHistory(history) {
      if (history && history.length > 0) {
        for (const item of history) {
          // user_id로 중복 여부 확인
          const isDuplicate = this.form.참석자명단.some((existing) => existing.user_id === item.user_id);

          // 중복이 아닌 경우에만 추가
          if (!isDuplicate) {
            const 서명 = item.raw.user && item.raw.user.signature ? item.raw.user.signature.path : "";
            this.form.참석자명단.push({
              id: Math.random().toString(36).substr(2, 9),
              업체명: item.업체명,
              소속: item.소속,
              직종: item.직종,
              이름: item.이름,
              휴대폰번호: item.휴대폰번호,
              서명,
              비고: "",
              user_id: item.user_id,
              직책: item.직책,
            });
          }
        }
      }
    },
    onSelectDailySafeWorkPermit(data) {
      console.log("data", data);
      this.form.stdInfo.field_id = data.field_id;
      this.form.stdInfo.field_name = data.사업장명;
      this.form.TBM정보.postId = data.postId;
      this.form.TBM정보.boardId = data.boardId;
      this.form.TBM정보.TBM실시일 = data.작업시작일시;
      this.form.TBM정보.TBM실시시간 = { sTime: data.시작시간 || "", eTime: "" };
      this.form.TBM정보.작업날짜와동일함 = "예";
      this.form.TBM정보.작업명 = data.작업허가명;
      this.form.TBM정보.작업내용 = data.작업내용;
      this.form.TBM정보.TBM장소 = data.작업장소;
      this.form.TBM정보.raw = data;

      this.form.TBM정보.폭염작업여부 = data.폭염작업여부 || "N";
      this.form.TBM정보.폭염작업 = data.폭염작업 || [];
      this.form.TBM정보.밀폐공간작업여부 = data.밀폐공간작업여부 ? "Y" : "N";
      this.form.TBM정보.밀폐공간작업 = data.밀폐공간작업 || {};

      // 작성페이지의 상단 버튼들을 다시 그릴 수 있도록 이벤트를 발생시킨다.
      const event = new CustomEvent("refreshButtons");
      window.dispatchEvent(event);
    },

    onClickCancel() {
      this.form.stdInfo.field_id = 0;
      this.form.stdInfo.field_name = "";
      this.form.TBM정보.postId = 0;
      this.form.TBM정보.boardId = 0;
      this.form.TBM정보.TBM실시일 = "";
      this.form.TBM정보.TBM실시시간 = { sTime: "", eTime: "" };
      this.form.TBM정보.작업날짜와동일함 = "아니오";
      this.form.TBM정보.작업명 = "";
      this.form.TBM정보.작업내용 = "";
      this.form.TBM정보.TBM장소 = "";
      this.form.TBM정보.raw = {};
      this.form.TBM정보.폭염작업여부 = "N";
      this.form.TBM정보.폭염작업 = [];
      this.form.TBM정보.밀폐공간작업여부 = "N";
      this.form.TBM정보.밀폐공간작업 = {};
    },

    // 복사하기 기능을 사용하여, 복사된 게시글의 내용을 수정할 때 호출
    // (URL query 에 _cp_pid 가 있을 경우 호출)
    // watch 로 처리하지 않은 이유는, watch 로 처리하면, 필요 없는 데이터를 초기화 하는 과정에서 불필요하게 화면을 갱신하기 때문 (무한반복하는 경우도 있음)
    cpPostContent(contents) {
      const body = contents.body;
      const form = {
        ...this.form,
      };

      const keys = Object.keys(this.form);
      for (const key of keys) {
        if (body[key]) {
          form[key] = body[key];
        }
      }

      // 연결된 일 작업허가서 데이터는 복사하지 않음
      form.stdInfo.field_id = 0;
      form.stdInfo.field_name = "";
      form.TBM정보.postId = 0;
      form.TBM정보.boardId = 0;
      form.TBM정보.TBM실시일 = "";
      form.TBM정보.TBM실시시간 = { sTime: "", eTime: "" };
      form.TBM정보.작업날짜와동일함 = "아니오";
      // form.TBM정보.작업명 = '';
      // form.TBM정보.작업내용 = '';
      // form.TBM정보.TBM장소 = '';
      form.TBM정보.raw = {};
      form.TBM리더확인 = {
        소속명: "",
        직책: "",
        성명: "",
        서명: "",
      };

      // 참석자명단 데이터는 복사하지 않음
      form.참석자명단 = [];
      // 사진대지 데이터는 복사하지 않음
      form.photoList = [];
      // 휴게사진 데이터는 복사하지 않음
      form.photoList2 = [];

      // 참석자명단/사진대지/TBM실시시간을 초기화했으므로, TBM상태도 "미진행"으로 함께 초기화한다.
      // (이 함수는 watch 를 거치지 않고 plain object 를 만들어 반환하므로 자동 계산되지 않는다)
      form.TBM상태 = "미진행";

      contents.body = form;
      return contents;
    },

    setNormalBoardButtons(template) {
      const 안전작업허가서불러오기 = {
        shouldShow: { mode: "c|u", freePass: 0, permission: "c|u", onlyWriter: true },
        component: "vs-button",
        props: { class: "col-span-2", color: "secondary", type: "border" },
        content: "일 안전작업허가서 불러오기",
        emits: {
          click: (/*func*/) => {
            if (this.form.TBM정보.TBM실시일) {
              this.showDailySafeWorkPermit = true;

              this.$nextTick(() => {
                // 이벤트버스를 통해서 검색어가 변경되었을 때, 리스트를 다시 불러온다
                if (this.$eventbus) {
                  this.$eventbus.$emit("searchItem-setInitData");
                }
                this.$refs.popupDailySafeWorkPermit.loadPost();
              });
            } else {
              alert("TBM 정보의 TBM 실시일을 선택해주세요.");
            }
          },
        },
        tooltip: "일안전작업허가서를 작성 완료한 경우 해당 시 일안전작업허가서를 불러와야 합니다",
      };

      // // 일 안전작업허가서 불러오기를 했을 경우, 복사하기를 할 수 없도록 막기
      // template.copy.shouldShow.freePass = this.form.TBM정보.postId > 0 ? -1 : 0;

      // this.readonly === false 일 때에만 "안전작업허가서불러오기" 항목을 추가한다
      const buttonList = [];
      if (!this.readonly) {
        buttonList.push(안전작업허가서불러오기);
      }
      buttonList.push(template.print, template.delete, template.move, template.save, template.copy);
      return buttonList;
    },

    onClickLoadQR() {
      if (this.form.TBM정보.TBM실시일) {
        this.showQRHistory = true;
      } else {
        alert("TBM 정보의 TBM 실시일을 선택해주세요.");
      }
    },

    // 온습도 관련
    /**
     * 습구온도(Tw) 계산
     * @param {number} ta 기온 (℃)
     * @param {number} rh 상대습도 (%)
     * @returns {number} 습구온도
     */
    getTw(ta, rh) {
      // 습구온도 계산공식 (Java 코드와 동일)
      return ta * Math.atan(0.151977 * Math.pow(rh + 8.313659, 0.5)) + Math.atan(ta + rh) - Math.atan(rh - 1.67633) + 0.00391838 * Math.pow(rh, 1.5) * Math.atan(0.023101 * rh) - 4.686035;
    },

    /**
     * 여름철 체감온도 계산 (5월 ~ 9월)
     * @param {number} ta 기온 (℃)
     * @param {number} rh 상대습도 (%)
     * @returns {number} 체감온도
     */
    getInSummer(ta, rh) {
      const tw = this.getTw(ta, rh);
      return -0.2442 + 0.55399 * tw + 0.45535 * ta - 0.0022 * Math.pow(tw, 2.0) + 0.00278 * tw * ta + 3.0;
    },

    /**
     * 겨울철 체감온도 계산 (10월 ~ 익년 4월)
     * @param {number} ta 기온 (℃)
     * @param {number} v 10분 평균 풍속 (m/s) - 풍속이 없는 경우 0.5를 기본값으로 사용
     * @returns {number} 체감온도
     */
    getInWinter(ta, v = 0.5) {
      return 13.12 + 0.6215 * ta - 11.37 * Math.pow(v, 0.16) + 0.3965 * Math.pow(v, 0.16) * ta;
    },

    /**
     * 현재 월수 출력
     * @returns {number} 현재 월 (1-12)
     */
    getCurrentSeason() {
      return new Date().getMonth() + 1; // getMonth()는 0부터 시작하므로 +1
    },

    /**
     * 값의 범위를 제한하는 함수
     * @param {number} value 입력값
     * @param {number} min 최소값
     * @param {number} max 최대값
     * @returns {number} 제한된 값
     */
    clampValue(value, min, max) {
      return Math.max(min, Math.min(max, value));
    },

    // 체감온도 계산 함수 (캡슐화된 버전)
    calculateTemperatureFeel(ta, rh) {
      ta = Number(ta);
      rh = Number(rh);
      if (!ta || isNaN(ta)) {
        ta = 0;
      }
      if (!rh || isNaN(rh)) {
        rh = 0;
      }

      // 온도와 습도 값을 범위 내로 제한
      ta = this.clampValue(ta, -20, 40); // 온도: -20℃ ~ 40℃
      rh = this.clampValue(rh, 0, 100); // 습도: 0% ~ 100%

      // 유효하지 않은 값인 경우 체감온도를 0으로 설정
      if (ta < -20 || ta > 40 || rh < 0 || rh > 100) {
        return 0;
      }

      // 현재 계절에 따라 체감온도 계산 공식 적용
      const season = this.getCurrentSeason();
      let temperatureFeel = 0;

      if (season >= 5 && season <= 9) {
        // 여름철 체감온도 계산 (5월~9월)
        temperatureFeel = this.getInSummer(ta, rh);
      } else {
        // 겨울철 체감온도 계산 (10월~4월)
        // 풍속이 없으므로 기본값 0.5 m/s 사용
        temperatureFeel = this.getInWinter(ta, 0.5);
      }

      return parseFloat(temperatureFeel.toFixed(1));
    },

    /**
     * 체감온도 계산 (배열 아이템용)
     * @param {Object} item 폭염작업 배열의 개별 아이템
     * @param {number} index 배열 인덱스
     */
    // 체감온도 계산 함수
    calculateHeatWorkFeelTemperature(value, rowData, cellData, rowIndex) {
      const feelTemp = this.calculateTemperatureFeel(rowData.온도, rowData.습도);
      this.$set(this.form.TBM정보.폭염작업[rowIndex], "체감온도", feelTemp);
    },

    updateHeatWorkReadonly(rowIndex, temperature) {
      if (temperature && temperature !== "") {
        // 체감온도가 입력되면 해당 행을 readonly 목록에 추가
        if (!this.heatWorkReadonlyRows.includes(rowIndex)) {
          this.heatWorkReadonlyRows.push(rowIndex);
        }
      } else {
        // 체감온도가 비어있으면 readonly 목록에서 제거
        this.heatWorkReadonlyRows = this.heatWorkReadonlyRows.filter((index) => index !== rowIndex);
      }
    },

    validate() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        if (this.$v.form.TBM정보.폭염작업.$invalid && this.$v.form.TBM정보.폭염작업.heatWorkValidation === false) {
          return "폭염작업의 대상이 아닙니다 N으로 변경 바랍니다";
        }
      }
    },

    confinedSpaceTrainingItems(items) {
      for (const item of items) {
        if (!this.form.TBM정보.밀폐공간작업[item.code]) {
          this.form.TBM정보.밀폐공간작업[item.code] = { isChecked: item.isChecked, 비고: item.비고 };
        }
        this.form.TBM정보.밀폐공간작업[item.code].isChecked = item.isChecked;
        this.form.TBM정보.밀폐공간작업[item.code].비고 = item.비고;
      }
    },

    // DB 값을 UI 모델로 변환 (초기 로드 시에만 사용)
    loadConfinedSpaceTrainingFromDB() {
      const dbData = this.form.TBM정보.밀폐공간작업;
      if (dbData && Object.keys(dbData).length > 0) {
        // 깊은 복사로 원본 데이터 보호
        const 밀폐공간교육항목 = this.밀폐공간교육항목.map((item) => ({
          ...item,
          isChecked: false,
          비고: "",
        }));

        // DB 데이터를 UI 모델에 매핑 (code 기반으로 안전하게 매핑)
        밀폐공간교육항목.forEach((item) => {
          if (dbData[item.code]) {
            item.isChecked = dbData[item.code].isChecked || false;
            item.비고 = dbData[item.code].비고 || "";
          }
        });

        this.밀폐공간교육항목 = 밀폐공간교육항목;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;

  &,
  th,
  td {
    border: 1px solid #cdcdcd;
    padding: 15px 0;
  }

  th {
    font-weight: 500;
    font-size: 14px;
    text-align: center;
    background: #f9f9f9;
  }

  td {
    text-align: center;
  }
}
</style>
